import { promises as fs } from 'node:fs';
import tmp from 'tmp-promise';
import type { Configuration } from '../types';
import { GenerationError } from '../types';

export class VivliostyleGenerator {
  private config: Configuration;

  constructor(config: Configuration) {
    this.config = config;
  }

  async generate(html: string): Promise<Buffer> {
    try {
      console.log(
        `VivliostyleGenerator: Generating PDF from HTML - Format: ${this.config.page.format}`
      );

      // Create temporary files for HTML input and PDF output
      const htmlFile = await tmp.file({ postfix: '.html' });
      const pdfFile = await tmp.file({ postfix: '.pdf' });

      try {
        // Enhance HTML with page CSS and metadata
        const enhancedHtml = this.enhanceHtmlForPrint(html);

        // Write HTML to temporary file
        await fs.writeFile(htmlFile.path, enhancedHtml, 'utf-8');

        // Import Vivliostyle CLI programmatically
        const { build } = await import('@vivliostyle/cli');

        // Configure Vivliostyle build options following the correct API
        const buildOptions = {
          input: htmlFile.path,
          output: pdfFile.path,
          size: this.config.page.format,
          timeout: 60000,
          logLevel: 'silent' as const,
          singleDoc: true,
          pressReady: this.config.pdf.pdfA,
        };

        console.log(`VivliostyleGenerator: Building PDF with Vivliostyle CLI`);

        // Execute Vivliostyle build
        await build(buildOptions);

        // Read the generated PDF
        const pdfBuffer = await fs.readFile(pdfFile.path);

        console.log(`VivliostyleGenerator: Successfully generated PDF (${pdfBuffer.length} bytes)`);

        return pdfBuffer;
      } finally {
        // Clean up temporary files
        try {
          await htmlFile.cleanup();
          await pdfFile.cleanup();
        } catch (cleanupError) {
          console.warn('VivliostyleGenerator: Failed to cleanup temporary files:', cleanupError);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('VivliostyleGenerator: PDF generation failed:', errorMessage);

      // In test environment, browser conflicts are common - provide helpful error context
      if (
        errorMessage.includes('ERR_CONNECTION_REFUSED') ||
        errorMessage.includes('Target page, context or browser has been closed') ||
        errorMessage.includes('Protocol error')
      ) {
        console.warn(
          'VivliostyleGenerator: Browser conflict detected (common in test environments)'
        );
        console.warn(
          'VivliostyleGenerator: Consider using PuppeteerGenerator fallback or running tests sequentially'
        );
      }

      throw new GenerationError(`Vivliostyle generation failed: ${errorMessage}`, {
        cause: error as Error,
      });
    }
  }

  /**
   * Enhance HTML with print-specific styling and metadata
   */
  private enhanceHtmlForPrint(html: string): string {
    // Add print-specific CSS to the HTML
    const printCSS = this.buildPageCSS();

    // Add accessibility metadata and structure
    const accessibilityMeta = this.buildAccessibilityMeta();
    const structuredHtml = this.addAccessibilityStructure(html);

    // Inject the CSS and accessibility features into the HTML document
    if (structuredHtml.includes('</head>')) {
      return structuredHtml.replace(
        '</head>',
        `${accessibilityMeta}\n<style>${printCSS}</style>\n</head>`
      );
    } else if (structuredHtml.includes('<head>')) {
      return structuredHtml.replace(
        '<head>',
        `<head>\n${accessibilityMeta}\n<style>${printCSS}</style>`
      );
    } else {
      // If no head section, wrap the content with full structure
      return `<!DOCTYPE html>
<html lang="${this.config.document.language || 'en'}">
<head>
<meta charset="utf-8">
${accessibilityMeta}
<style>${printCSS}</style>
</head>
<body role="document">
${structuredHtml}
</body>
</html>`;
    }
  }

  /**
   * Build accessibility metadata for PDF/UA compliance
   */
  private buildAccessibilityMeta(): string {
    const title = this.config.document.title || 'Document';
    const author = this.config.document.author || '';
    const description = this.config.document.subject || '';
    const lang = this.config.document.language || 'en';

    return `
<!-- PDF/UA Accessibility Metadata -->
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta name="description" content="${this.escapeHtml(description)}">
<meta name="author" content="${this.escapeHtml(author)}">
<meta name="dcterms.title" content="${this.escapeHtml(title)}">
<meta name="dcterms.creator" content="${this.escapeHtml(author)}">
<meta name="dcterms.language" content="${lang}">
<meta name="pdf:Tagged" content="true">
<meta name="pdf:PDFVersion" content="1.7">
<meta name="pdf:UA-Identifier" content="PDF/UA-1">
<title>${this.escapeHtml(title)}</title>`;
  }

  /**
   * Add accessibility structure tags to HTML content
   */
  private addAccessibilityStructure(html: string): string {
    let structured = html;

    // Ensure all headings have proper ARIA roles
    structured = structured.replace(/<h1([^>]*)>/gi, '<h1$1 role="heading" aria-level="1">');
    structured = structured.replace(/<h2([^>]*)>/gi, '<h2$1 role="heading" aria-level="2">');
    structured = structured.replace(/<h3([^>]*)>/gi, '<h3$1 role="heading" aria-level="3">');
    structured = structured.replace(/<h4([^>]*)>/gi, '<h4$1 role="heading" aria-level="4">');
    structured = structured.replace(/<h5([^>]*)>/gi, '<h5$1 role="heading" aria-level="5">');
    structured = structured.replace(/<h6([^>]*)>/gi, '<h6$1 role="heading" aria-level="6">');

    // Add navigation roles to table of contents
    structured = structured.replace(
      /<nav([^>]*)>/gi,
      '<nav$1 role="navigation" aria-label="Table of Contents">'
    );

    // Add figure roles and ensure images have alt text
    structured = structured.replace(/<figure([^>]*)>/gi, '<figure$1 role="figure">');
    structured = structured.replace(/<img(?![^>]*alt=)([^>]*)>/gi, '<img alt=""$1>');

    // Add table structure tags
    structured = structured.replace(/<table([^>]*)>/gi, '<table$1 role="table">');
    structured = structured.replace(/<thead([^>]*)>/gi, '<thead$1 role="rowgroup">');
    structured = structured.replace(/<tbody([^>]*)>/gi, '<tbody$1 role="rowgroup">');
    structured = structured.replace(/<th([^>]*)>/gi, '<th$1 role="columnheader">');

    // Add list structure tags
    structured = structured.replace(/<ul([^>]*)>/gi, '<ul$1 role="list">');
    structured = structured.replace(/<ol([^>]*)>/gi, '<ol$1 role="list">');
    structured = structured.replace(/<li([^>]*)>/gi, '<li$1 role="listitem">');

    // Add section landmarks
    structured = structured.replace(/<section([^>]*)>/gi, '<section$1 role="region">');
    structured = structured.replace(/<article([^>]*)>/gi, '<article$1 role="article">');
    structured = structured.replace(/<aside([^>]*)>/gi, '<aside$1 role="complementary">');

    // Add main content landmark if not present
    if (!structured.includes('role="main"') && !structured.includes('<main')) {
      structured = `<main role="main">${structured}</main>`;
    }

    // Ensure language attribute is set
    if (!structured.includes('lang=')) {
      const lang = this.config.document.language || 'en';
      structured = structured.replace(/<html([^>]*)>/gi, `<html$1 lang="${lang}">`);
    }

    return structured;
  }

  /**
   * Escape HTML special characters for safe insertion
   */
  private escapeHtml(text: string | undefined): string {
    if (!text) return '';
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (m) => map[m] || m);
  }

  /**
   * Build page CSS configuration from config with accessibility enhancements
   */
  private buildPageCSS(): string {
    const margins = this.config.page.margins;
    const title = this.config.document.title || '';

    return `
@page {
  size: ${this.config.page.format} ${this.config.page.orientation};
  margin-top: ${margins.top};
  margin-right: ${margins.right};
  margin-bottom: ${margins.bottom};
  margin-left: ${margins.left};

  /* PDF/UA Page structure */
  @top-left {
    content: "${this.escapeHtml(title)}";
    font-size: 9pt;
    color: #666;
  }

  @top-right {
    content: "Page " counter(page) " of " counter(pages);
    font-size: 9pt;
    color: #666;
  }
}

/* Accessibility enhancements */
:root {
  --min-contrast-ratio: 4.5;
  --focus-outline: 3px solid #0066cc;
}

/* Note: Body and link colors are defined by the theme CSS, not overridden here */
/* Themes handle their own color schemes including links */

/* Focus indicators for accessibility */
*:focus {
  outline: var(--focus-outline);
  outline-offset: 2px;
}

/* Skip navigation for screen readers */
.skip-nav {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.skip-nav:focus {
  position: static;
  width: auto;
  height: auto;
}

/* Ensure images have borders when alt text is missing */
img:not([alt]) {
  border: 2px solid red;
}

/* Print optimizations with accessibility */
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Theme CSS handles body styling - no overrides here */

  /* Ensure headings are distinguishable */
  h1 { font-size: 24pt; margin-top: 24pt; margin-bottom: 12pt; }
  h2 { font-size: 20pt; margin-top: 20pt; margin-bottom: 10pt; }
  h3 { font-size: 16pt; margin-top: 16pt; margin-bottom: 8pt; }
  h4 { font-size: 14pt; margin-top: 14pt; margin-bottom: 7pt; }
  h5 { font-size: 12pt; margin-top: 12pt; margin-bottom: 6pt; }
  h6 { font-size: 11pt; margin-top: 11pt; margin-bottom: 5pt; }

  h1, h2, h3, h4, h5, h6 {
    page-break-after: avoid;
    break-after: avoid;
    font-weight: bold;
  }

  /* Maintain reading flow */
  p {
    orphans: 3;
    widows: 3;
  }

  /* Page breaks - layout only, no styling */
  pre, blockquote {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  img, figure {
    page-break-inside: avoid;
    break-inside: avoid;
    max-width: 100%;
    height: auto;
  }

  table, tr {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Code styling handled by theme CSS - no overrides */

  /* Theme handles link styling - no overrides */
}`;
  }
}
